//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { LoadingBar } from '@/components/_common';
import { Container, Grid, Fade } from '@mui/material';
import { clearJob, getJob, getJobRelatedFiles } from '@/redux/slices/jobSlice';
import { selectJob, selectJobIsLoading } from '@/redux/selectors/jobSelectors';
import { clearTitle as clearTitleV1, getTitleDetails, getTitleVersions as getTitleVersionsV1 } from '@/redux/slices/titleSlice';
import { clearTitleSlice as clearTitleV2, fetchTitleV2, getTitleVersions as getTitleVersionsV2 } from '@/redux/slices/titleV2Slice';
import { setCurrentTable } from '@/redux/slices/dataTableSlice';
import { useQuery } from '@/components/_hooks/useQuery';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { ConfigContext } from '@/contextAPI/configContext';
import { PAGE_SIZE } from '@/constants/constants';
import JobTimeline from './JobTimeline/JobTimeline';
import JobTitleBar from './JobTitleBar';
import JobTabs from './JobTabs/JobTabs';

const DefaultTabs = ['assets', 'additional', 'tasks', 'title', 'files'];

export type JobTabPagination = {
  pageSize: number;
  pageNum: number;
  textFiltered?: boolean;
};

const Job = () => {
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const refresh = useContext(ConfigContext)?.jobDetails?.refreshCache;
  const showFullInputs = useContext(ConfigContext)?.jobDetails?.showFullInputs;
  const [tab, setTab] = useState(0);
  const loading = useAppSelector(selectJobIsLoading);
  const { title, bundle_job_hid, job_type } = useAppSelector(selectJob);
  const [showAllFiles, setShowAllFiles] = useState(false);
  const Tabs = useMemo(
    () => [...DefaultTabs, showFullInputs ? 'full_inputs' : null, bundle_job_hid || job_type === 'bundle' ? 'bundle' : null].filter(Boolean),
    [bundle_job_hid, job_type, showFullInputs]
  );

  const [pagination, setPagination] = useState<JobTabPagination>({
    pageSize: PAGE_SIZE,
    pageNum: 1,
    textFiltered: false
  });

  const query = useQuery();
  const selectedTab = query.get('tab');

  useEffect(() => {
    dispatch(setCurrentTable('jobs'));
    dispatch(getJob({ id }))
      .unwrap()
      .catch(() => history.push('/page-not-found'));
    loadJobRelatedFiles(id, showAllFiles, pagination);
    return () => {
      dispatch(clearJob());
      dispatch(isTitleV2 ? clearTitleV2() : clearTitleV1());
    };
  }, [id]);

  const handleTabChange = (selectedTabIndex: number) => {
    setTab(selectedTabIndex);
    history.replace({
      search: '?' + new URLSearchParams({ tab: Tabs[selectedTabIndex] }).toString()
    });
  };

  const toggleShowAllFiles = async () => {
    const newShowAll = !showAllFiles;
    setShowAllFiles(newShowAll);
    setPagination((prev) => ({ ...prev, pageNum: 1 }));

    loadJobRelatedFiles(id, newShowAll, {
      ...pagination,
      pageNum: 1
    });
  };

  const updatePagination = (newPagination: JobTabPagination) => {
    setPagination(newPagination);
    if (!pagination.textFiltered || !newPagination.textFiltered) {
      loadJobRelatedFiles(id, showAllFiles, newPagination);
    }
  };

  const loadJobRelatedFiles = (id: string, showAll: boolean, { pageSize, pageNum, textFiltered }: JobTabPagination) => {
    let updatedPageSize = pageSize;
    if (textFiltered) {
      updatedPageSize = 10000;
    }

    dispatch(getJobRelatedFiles({ id, showAll, pageSize: updatedPageSize, pageNum: pageNum - 1 }));
  };

  const handleRefresh = () => {
    let request: { id: string; refresh?: boolean } = { id };
    if (refresh) {
      request = { ...request, refresh: true };
    }
    dispatch(getJob(request))
      .unwrap()
      .catch(() => history.push('/page-not-found'));
    // Fetching title details and versions required for TitleDetailMetadata and TitleDetailSynopsis that used deeper down component tree
    if (isTitleV2) {
      dispatch(fetchTitleV2(title.id));
      dispatch(getTitleVersionsV2(title.id));
    } else {
      dispatch(getTitleDetails(title.id));
      dispatch(getTitleVersionsV1(title.id));
    }
    loadJobRelatedFiles(id, showAllFiles, pagination);
  };

  useEffect(() => {
    if (selectedTab && Tabs.indexOf(selectedTab) > -1) {
      setTab(Tabs.indexOf(selectedTab));
    }
  }, [selectedTab, Tabs]);

  return (
    <Fade in timeout={600}>
      <div>
        <LoadingBar loading={loading} />
        <JobTitleBar onRefresh={handleRefresh} />
        <Container>
          <Grid container spacing={1}>
            <Grid item xs={12} md={8} lg={9}>
              <JobTabs
                tab={tab}
                handleTabChange={handleTabChange}
                showAllFiles={showAllFiles}
                toggleShowAllFiles={toggleShowAllFiles}
                pagination={pagination}
                updatePagination={updatePagination}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <JobTimeline />
            </Grid>
          </Grid>
        </Container>
      </div>
    </Fade>
  );
};

export default Job;
